import { FormikErrors, useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../components/Input/Input';
import {
  getAmazonConnectCredentials,
  getUser,
  getUserCompanies,
} from '../../core/api';
import { confirmSignUp, login } from '../../core/auth';
import { isCodeValid, setTokensToLocalStorage } from '../../core/helpers';
import { useAppDispatch, useAuth } from '../../hooks';
import { Loader } from '../../components/Loader/Loader';
import { useHistory } from 'react-router-dom';
import { setFreshUser } from '../../core/store/user/userSlice';
import Button from '../../components/Button';

interface FormValues {
  code: string;
}

interface Props {
  email: string;
  password: string;
}

export default function RegisterCustomerVerificationForm({
  email,
  password,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { authUser } = useAuth();

  const formik = useFormik<FormValues>({
    validateOnBlur: true,
    initialValues: {
      code: '',
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};

      if (!values.code) {
        errors.code = 'Required';
      }
      if (values.code !== '' && !isCodeValid(values.code)) {
        errors.code = 'Verification code is not valid';
      }

      return errors;
    },
    onSubmit: ({ code }) => {
      setIsLoading(true);
      if (error) setError('');
      confirmSignUp({
        email,
        confirmationCode: code,
      })
        .then(() => login({ email, password }))
        .then((res) => {
          setTokensToLocalStorage(
            res.AuthenticationResult!.AccessToken!,
            res.AuthenticationResult!.RefreshToken!,
            res.AuthenticationResult!.IdToken!
          );

          return Promise.all([
            getUser(),
            getAmazonConnectCredentials(),
            getUserCompanies(),
          ]);
        })
        .then((responses) => {
          dispatch(setFreshUser(true));
          authUser(responses);
          setIsLoading(false);
          history.push('/');
        })
        .catch((err) => {
          setIsLoading(false);
          if (err.name === 'CodeMismatchException') {
            setError('Invalid verification code provided, please try again.');
          } else {
            setError('Something went wrong, please try again');
          }
        });
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      {isLoading && <Loader />}
      <div className='text-center Text'>
        <div className='Title fw-900 mb-2'>{t('Verify your email')}</div>
        <div
          data-testid='code-text'
          dangerouslySetInnerHTML={{
            __html: t('Please enter the 6 digits we sent you to').replace(
              '*email*',
              `<span className='text-nowrap'>${email}</span>`
            ),
          }}
        />
      </div>
      <div className='d-flex align-items-center justify-content-center flex-column mx-auto mt-3 mt-lg-4 RegisterCustomer__codeBlock'>
        <Input
          name='code'
          id='code'
          type='text'
          className='RegisterCustomer__codeInput'
          placeholder='******'
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.code}
          error={formik.errors.code}
          touched={formik.touched.code}
        />
        <Button className='Button--lg RegisterForm__btn w-100' type='submit'>
          {t('Verify email')}
        </Button>
      </div>
      {error && (
        <div className='RegisterForm__error mt-2 text-center'>{t(error)}</div>
      )}
    </form>
  );
}
